import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AuthUser, LoginPayload } from '../types/auth.types'
import { authService, AuthRequestError } from '../services/auth.service'

const TOKEN_STORAGE_KEY = 'studiopro_token'
/** Zuletzt bekannter Nutzer (Rolle, Verknüpfungen, Name - keine Geheimnisse): damit der Offline-Übungsbereich ohne Server startet */
const USER_STORAGE_KEY = 'studiopro_user'

function readCachedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

function cacheUser(user: AuthUser | null): void {
  try {
    if (user) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_STORAGE_KEY)
  } catch {
    // Speicher gesperrt/voll - dann eben ohne Offline-Start
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY))
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** true = angemeldet, aber der Server war beim Start nicht erreichbar (nur der Offline-Bereich ist nutzbar) */
  const offlineSession = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isTeacher = computed(() => user.value?.role === 'teacher')
  const isStudent = computed(() => user.value?.role === 'student')
  const isGuardian = computed(() => user.value?.role === 'guardian')

  async function login(payload: LoginPayload): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      const result = await authService.login(payload)
      user.value = result.user
      token.value = result.token
      offlineSession.value = false
      localStorage.setItem(TOKEN_STORAGE_KEY, result.token)
      cacheUser(result.user)
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    if (token.value) {
      try {
        await authService.logout(token.value)
      } catch {
        // Serverseitiges Widerrufen ist best-effort - lokal wird ohnehin abgemeldet
      }
    }
    // Offline gespeicherte Übungsdateien gehören zum Konto: auf einem geteilten Gerät nicht für den Nächsten liegen lassen
    const userId = user.value?.id
    if (userId) {
      void import('@/modules/practice/offline/offline-store').then((m) => m.clearOfflineData(userId)).catch(() => {})
    }
    user.value = null
    token.value = null
    offlineSession.value = false
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    cacheUser(null)
  }

  /** Wirft bei Fehler (Meldung fürs Formular); bei Erfolg läuft die Sitzung mit dem neuen Token weiter. */
  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!token.value) throw new Error('Nicht angemeldet')
    const newToken = await authService.changePassword(token.value, currentPassword, newPassword)
    token.value = newToken
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken)
  }

  // Beim App-Start: vorhandenes Token gegen das Backend prüfen, statt es
  // blind als gültig anzunehmen (abgelaufen/widerrufen wird sonst erst beim
  // nächsten API-Aufruf bemerkt).
  async function restoreSession(): Promise<void> {
    if (!token.value) return
    try {
      user.value = await authService.me(token.value)
      offlineSession.value = false
      cacheUser(user.value)
    } catch (err) {
      // Server nicht erreichbar (offline, Funkloch, Neustart beim Deploy): angemeldet bleiben und mit dem
      // zuletzt bekannten Nutzer in den Offline-Bereich - nur ein ungültiges Token beendet die Sitzung
      const unreachable = err instanceof AuthRequestError && (err.status === 0 || err.status >= 500)
      const cached = unreachable ? readCachedUser() : null
      if (cached) {
        user.value = cached
        offlineSession.value = true
        return
      }
      token.value = null
      user.value = null
      offlineSession.value = false
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      cacheUser(null)
    }
  }

  function getRedirectPath(): string {
    switch (user.value?.role) {
      case 'admin':
        return '/dashboard/admin'
      case 'teacher':
        return '/dashboard/teacher'
      case 'student':
        return '/dashboard/student'
      case 'guardian':
        return '/dashboard/guardian'
      default:
        return '/login'
    }
  }

  return {
    user,
    token,
    isLoading,
    error,
    offlineSession,
    isAuthenticated,
    isAdmin,
    isTeacher,
    isStudent,
    isGuardian,
    login,
    logout,
    changePassword,
    restoreSession,
    getRedirectPath
  }
})
